/**
 * Group quota (plan 4.4, FR-ORG-01, NFR-CAP-03): the user and operation
 * quotas of a company are totals across all of its legal entities, and every
 * active license adds its capacity linearly.
 *
 *   groupUsage/{tenantId}  operations, storageUsedBytes,
 *                          byLegalEntity.{id}.operations / .storageUsedBytes
 */

import { doc, getDoc, setDoc, increment, serverTimestamp } from "./fs";
import { db } from "../config/firebase";
import { GB, DEFAULT_OPERATION_LIMIT, DEFAULT_STORAGE_LIMIT_GB, evaluateQuota } from "./quota";
import { getTenancy } from "./tenancy";
import { isDemoMode } from "./demoMode";

export const DEFAULT_LICENSE_USERS = 5;
export const NO_LEGAL_ENTITY = "_none";

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);

function expiryMillis(license) {
  const v = license?.expiresAt;
  if (!v) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const ms = new Date(v).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Revoked, suspended or expired licenses add no capacity. */
export function isLicenseActive(license, now = Date.now()) {
  if (!license) return false;
  if (["revoked", "suspended", "cancelled", "inactive"].includes(String(license.status || "").toLowerCase())) return false;
  const exp = expiryMillis(license);
  return exp == null || exp > now;
}

export function licenseCapacity(license) {
  return {
    users: num(license?.maxUsers) || DEFAULT_LICENSE_USERS,
    operations: num(license?.operationLimit) || DEFAULT_OPERATION_LIMIT,
    storageBytes: (num(license?.storageLimitGb) || num(license?.storageLimit) || DEFAULT_STORAGE_LIMIT_GB) * GB
  };
}

/** Sum of the active licenses' capacity; a company without a license gets one default license. */
export function groupCapacity(licenses = [], now = Date.now()) {
  const active = licenses.filter((l) => isLicenseActive(l, now));
  const parts = active.length ? active.map(licenseCapacity) : [licenseCapacity(null)];
  return parts.reduce((acc, c) => ({
    users: acc.users + c.users,
    operations: acc.operations + c.operations,
    storageBytes: acc.storageBytes + c.storageBytes,
    licenses: acc.licenses
  }), { users: 0, operations: 0, storageBytes: 0, licenses: active.length });
}

/** Users that count against the group's user quota (disabled accounts do not). */
export function countGroupUsers(users = [], tenantId) {
  return users.filter((u) => (u.id === tenantId || u.parentUid === tenantId) && u.disabled !== true).length;
}

/** Quota state of the group, in the shape `evaluateQuota` reads. */
export function groupUsageState(raw = {}, capacity) {
  return {
    opsUsed: num(raw.operations),
    opsLimit: capacity.operations,
    bytesUsed: num(raw.storageUsedBytes),
    bytesLimit: capacity.storageBytes
  };
}

/** Rows for the admin panel: per legal entity operations, storage and assigned users. */
export function legalEntityBreakdown(raw = {}, legalEntities = [], scopes = [], users = []) {
  const by = raw.byLegalEntity || {};
  const rows = legalEntities.map((le) => ({
    id: le.id,
    code: le.code,
    name: le.name,
    operations: num(by[le.id]?.operations),
    storageBytes: num(by[le.id]?.storageUsedBytes),
    users: users.filter((u) => {
      const s = scopes.find((x) => x.userId === u.id || x.id === u.id);
      return !s || s.allLegalEntities || (s.legalEntityIds || []).includes(le.id);
    }).length
  }));
  const none = by[NO_LEGAL_ENTITY];
  if (none && (num(none.operations) || num(none.storageUsedBytes))) {
    rows.push({ id: NO_LEGAL_ENTITY, code: "—", name: null, operations: num(none.operations), storageBytes: num(none.storageUsedBytes), users: 0 });
  }
  return rows;
}

export function assertUserCapacity(currentUsers, capacity) {
  if (currentUsers + 1 > capacity.users) {
    const err = new Error("QUOTA_USERS");
    err.code = "QUOTA_USERS";
    err.usage = { users: currentUsers, limit: capacity.users };
    throw err;
  }
}

// Capacity of the signed-in user's group; AuthContext loads it from the licenses.
let currentCapacity = null;

export function setGroupCapacity(capacity) {
  currentCapacity = capacity || null;
}

export function getGroupCapacity() {
  return currentCapacity;
}

export const GroupUsageService = {
  async get(tenantId) {
    if (!tenantId) return {};
    const snap = await getDoc(doc(db, "groupUsage", tenantId)).catch(() => null);
    return snap?.exists() ? snap.data() : {};
  },

  /** Adds one operation (and bytes) to the group total and to the active legal entity. */
  async record(tenantId, { ops = 1, bytes = 0, legalEntityId = getTenancy().legalEntityId } = {}) {
    if (!tenantId || isDemoMode()) return;
    const le = legalEntityId || NO_LEGAL_ENTITY;
    await setDoc(doc(db, "groupUsage", tenantId), {
      operations: increment(ops),
      storageUsedBytes: increment(bytes),
      byLegalEntity: { [le]: { operations: increment(ops), storageUsedBytes: increment(bytes) } },
      updatedAt: serverTimestamp()
    }, { merge: true });
  },

  /** Quota result for the whole group if `bytes` / `ops` more were used. */
  async check(tenantId, capacity, { bytes = 0, ops = 1 } = {}) {
    const raw = await this.get(tenantId);
    const usage = groupUsageState(raw, capacity);
    return { usage, ...evaluateQuota(usage, { bytes, ops }) };
  }
};
